import { DateTime } from 'luxon'
import { computed, getCurrentInstance, onMounted, onScopeDispose, shallowRef } from 'vue'
import type { ComputedRef } from 'vue'

import { describeTodayServiceHours } from '~/composables/describeTodayServiceHours'
import type { TranslateFn } from '~/composables/TranslateFn'
import { usePresentationTranslator } from '~/composables/usePresentationTranslator'
import { DayOfWeekValues } from '~/enums'
import type { PublicStoreDto } from '~/services/api/generated'
import { closesNextDay } from '~/utils/closesNextDay'
import { formatStoreTime } from '~/utils/formatStoreTime'
import { isOpenAllDay } from '~/utils/isOpenAllDay'
import { normalizeStoreTimeForInput } from '~/utils/normalizeStoreTimeForInput'

const CLOCK_REFRESH_MS = 60_000

type StoreOperatingHours = NonNullable<PublicStoreDto['operating_hours']>
type StoreOperatingHour = StoreOperatingHours[number]
type StoreDay = (typeof DayOfWeekValues)[number]

interface WeeklyScheduleRow {
  key: string
  dayLabel: string
  hours: string
  isToday: boolean
}

interface ScheduleSource {
  operatingHours: () => StoreOperatingHours
  timezone: () => string | null | undefined
}

function pickHourForDay(hours: StoreOperatingHours, day: StoreDay): StoreOperatingHour | null {
  return hours.find((h) => h.day_of_week === day) ?? null
}

function timeToMinutes(value?: string | null): number | null {
  const normalized = normalizeStoreTimeForInput(value)
  if (!normalized) return null
  const [hours, minutes] = normalized.split(':').map(Number)
  if (
    hours === undefined ||
    minutes === undefined ||
    Number.isNaN(hours) ||
    Number.isNaN(minutes)
  ) {
    return null
  }
  return hours * 60 + minutes
}

/** One weekday's schedule row text: closed, unset, open all day, or its open–close range. */
function weeklyHoursLabel(hours: StoreOperatingHours, day: StoreDay, t: TranslateFn): string {
  const dayHour = pickHourForDay(hours, day)
  if (dayHour?.is_open !== true) return t('dashboard.labels.closed')
  const hasSetTimes =
    formatStoreTime(dayHour.open_time) !== 'N/A' && formatStoreTime(dayHour.close_time) !== 'N/A'
  if (!hasSetTimes) return 'Hours not set'
  return describeTodayServiceHours(hours, day, t).value
}

/** A still-running overnight session opened yesterday, as its "Open until" status. */
function overnightCarryoverStatus(
  hours: StoreOperatingHours,
  now: DateTime,
  nowMinutes: number
): string | null {
  const yesterdayKey = DayOfWeekValues[(now.weekday + 5) % 7]
  if (yesterdayKey === undefined) return null

  const yesterdayHour = pickHourForDay(hours, yesterdayKey)
  if (yesterdayHour?.is_open !== true) return null

  const yOpen = normalizeStoreTimeForInput(yesterdayHour.open_time)
  const yClose = normalizeStoreTimeForInput(yesterdayHour.close_time)
  const yCloseMinutes = timeToMinutes(yesterdayHour.close_time)
  if (closesNextDay(yOpen, yClose) && yCloseMinutes !== null && nowMinutes < yCloseMinutes) {
    return `Open until ${formatStoreTime(yesterdayHour.close_time)}`
  }
  return null
}

interface UseStoreScheduleSummaryReturn {
  todayStatus: ComputedRef<string | null>
  weeklySchedule: ComputedRef<WeeklyScheduleRow[]>
}

/** Today's open/closed status and the weekly schedule rows, read in the store's own timezone. */
export function useStoreScheduleSummary({
  operatingHours,
  timezone
}: ScheduleSource): UseStoreScheduleSummaryReturn {
  const t = usePresentationTranslator()

  // Refreshed once a minute while mounted so the status follows closing time and midnight.
  const clock = shallowRef(DateTime.now())
  let refreshTimer: ReturnType<typeof setInterval> | undefined
  const isInComponentSetup = getCurrentInstance() !== null
  if (isInComponentSetup) {
    onMounted(() => {
      clock.value = DateTime.now()
      refreshTimer = setInterval(() => {
        clock.value = DateTime.now()
      }, CLOCK_REFRESH_MS)
    })
  }
  onScopeDispose(() => {
    clearInterval(refreshTimer)
  })

  const storeNow = computed(() => {
    const tz = timezone()
    if (tz !== null && tz !== '') {
      const zoned = clock.value.setZone(tz)
      if (zoned.isValid) return zoned
    }
    return clock.value.toLocal()
  })

  const todayKey = computed(() => {
    const day = DayOfWeekValues[storeNow.value.weekday - 1]
    if (day === undefined) {
      throw new RangeError('Store timezone returned an invalid weekday')
    }
    return day
  })

  const todayStatus = computed((): string | null => {
    const hours = operatingHours()
    if (!hours.length) return null

    const now = storeNow.value
    const nowMinutes = now.hour * 60 + now.minute

    const carryover = overnightCarryoverStatus(hours, now, nowMinutes)
    if (carryover !== null) return carryover

    const todayHour = pickHourForDay(hours, todayKey.value)
    if (todayHour?.is_open !== true) return t('dashboard.labels.serviceHoursClosedToday')

    const open = normalizeStoreTimeForInput(todayHour.open_time)
    const close = normalizeStoreTimeForInput(todayHour.close_time)
    const openMinutes = timeToMinutes(todayHour.open_time)
    const closeMinutes = timeToMinutes(todayHour.close_time)
    if (openMinutes === null || closeMinutes === null) return t('dashboard.labels.open')

    if (isOpenAllDay(open, close)) return t('dashboard.labels.serviceHoursOpenAllDay')

    if (nowMinutes < openMinutes) {
      return `Opens at ${formatStoreTime(todayHour.open_time)}`
    }

    // Overnight hours close tomorrow, so the store is still open after today's open time.
    if (!closesNextDay(open, close) && nowMinutes >= closeMinutes) {
      return t('dashboard.labels.closed')
    }

    return `Open until ${formatStoreTime(todayHour.close_time)}`
  })

  const weeklySchedule = computed((): WeeklyScheduleRow[] => {
    const hours = operatingHours()
    if (!hours.length) return []

    return DayOfWeekValues.map((day) => ({
      dayLabel: day.charAt(0).toUpperCase() + day.slice(1),
      hours: weeklyHoursLabel(hours, day, t),
      isToday: day === todayKey.value,
      key: day
    }))
  })

  return { todayStatus, weeklySchedule }
}
